import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scanner_notifications } from '../services';
import { subHeading3, body, bodySmall, label, caption, fontWeight, spacing } from '../Constants/Fonts';
import { lightTheme } from '../Constants/Colors';

const STORAGE_KEY = 'nmt_notification_read_ids';

// Severity badge palettes (bg / text).
const SEVERITY = {
    critical: { background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7' },
    high:     { background: 'rgba(239, 68, 68, 0.15)',  color: '#ef4444' },
    medium:   { background: 'rgba(249, 115, 22, 0.15)', color: '#f97316' },
    low:      { background: 'rgba(34, 197, 94, 0.15)',  color: '#22c55e' },
};

// Inline styles (no SCSS). Typography from Fonts.js presets + spacing scale;
// colours from the light theme (lightTheme) in Colors.js so this panel matches
// the white app shell and the "What's New" popover exactly.
const badgeBase = {
    padding: `${spacing.xs}px ${spacing.md}px`, borderRadius: spacing.sm, letterSpacing: '0.4px',
};
const S = {
    wrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    btn: {
        background: lightTheme.surface, border: `1px solid ${lightTheme.border}`, padding: 0, cursor: 'pointer',
        color: lightTheme.textSecondary, width: 38, height: 38, borderRadius: lightTheme.radius, position: 'relative',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.15s ease',
    },
    // Small red presence dot (top-right) — signals unread without a count.
    dot: {
        position: 'absolute', top: 7, right: 8,
        width: 8, height: 8, borderRadius: '50%',
        background: lightTheme.danger,
        border: `2px solid ${lightTheme.surface}`, boxSizing: 'border-box',
    },
    panel: {
        position: 'absolute', top: 50, right: 0, width: 380, maxHeight: 520,
        background: lightTheme.surface, border: `1px solid ${lightTheme.border}`, borderRadius: spacing.xl,
        boxShadow: lightTheme.shadowLg, zIndex: 50,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
    },
    header: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: `${spacing['2xl']}px ${spacing['3xl']}px`,
        borderBottom: `1px solid ${lightTheme.border}`, background: lightTheme.surfaceMuted,
    },
    panelTitle: subHeading3({ fontWeight: fontWeight.bold, color: lightTheme.textPrimary }),
    markAll: bodySmall({
        fontWeight: fontWeight.semibold, background: 'transparent', border: 'none',
        color: lightTheme.primary, cursor: 'pointer', padding: `${spacing.sm}px ${spacing.md}px`,
        borderRadius: spacing.sm, transition: 'background 0.15s ease',
    }),
    panelBody: { flex: 1, overflowY: 'auto' },
    state: body({ padding: `${spacing['4xl']}px ${spacing['3xl']}px`, textAlign: 'center', color: lightTheme.textMuted }),
    stateError: body({ padding: `${spacing['4xl']}px ${spacing['3xl']}px`, textAlign: 'center', color: lightTheme.danger }),
    item: {
        padding: `${spacing['2xl']}px ${spacing['3xl']}px`,
        borderBottom: `1px solid ${lightTheme.border}`, cursor: 'pointer',
        transition: 'background 0.15s ease',
    },
    meta: { display: 'flex', alignItems: 'center', gap: spacing.lg, marginBottom: spacing.md, flexWrap: 'wrap' },
    newBadge: caption({ ...badgeBase, fontWeight: fontWeight.bold, background: lightTheme.successSoft, color: lightTheme.success }),
    category: label({ fontWeight: fontWeight.semibold, color: lightTheme.textSecondary, textTransform: 'uppercase', letterSpacing: '0.3px' }),
    date: label({ fontWeight: fontWeight.regular, color: lightTheme.textMuted, marginLeft: 'auto' }),
    title: body({ fontWeight: fontWeight.semibold, color: lightTheme.textPrimary, marginBottom: spacing.sm, lineHeight: 1.35 }),
    desc: bodySmall({ color: lightTheme.textSecondary, lineHeight: 1.5, marginBottom: spacing.lg }),
};

const severityStyle = (sev) => {
    const s = (sev || '').toLowerCase();
    const palette = SEVERITY[s];
    if (!palette) return null;
    return caption({ ...badgeBase, fontWeight: fontWeight.bold, textTransform: 'uppercase', ...palette });
};

const readReadIds = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeReadIds = (ids) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch {
        // noop
    }
};

const formatDate = (raw) => {
    if (!raw) return '';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const NotificationBell = () => {
    const [open, setOpen] = useState(false);
    const [readIds, setReadIds] = useState(readReadIds);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [btnHover, setBtnHover] = useState(false);
    const [markAllHover, setMarkAllHover] = useState(false);
    const [hoveredId, setHoveredId] = useState(null);
    const wrapperRef = useRef(null);
    const navigate = useNavigate();

    const loadNotifications = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await scanner_notifications();
            // Endpoint returns { items, total, limit, offset } directly (no response_data wrapper)
            const body = res?.statusCode;
            const items = Array.isArray(body?.items)
                ? body.items
                : Array.isArray(body?.response_data?.data?.items) ? body.response_data.data.items : [];
            setNotifications(items.filter(n => !n?.dismissed_at));
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError('Could not load notifications.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadNotifications();
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isItemRead = (n) => readIds.includes(String(n.id)) || !!n.read_at;
    const unreadCount = notifications.filter(n => !isItemRead(n)).length;

    const markAllRead = () => {
        const allIds = notifications.map(n => String(n.id));
        setReadIds(allIds);
        writeReadIds(allIds);
    };

    const markRead = (id) => {
        const key = String(id);
        if (readIds.includes(key)) return;
        const next = [...readIds, key];
        setReadIds(next);
        writeReadIds(next);
    };

    const handleAction = (n) => {
        markRead(n.id);
        if (n.link) {
            setOpen(false);
            navigate(n.link);
        }
    };

    return (
        <div style={S.wrapper} ref={wrapperRef}>
            <button
                type="button"
                style={{
                    ...S.btn,
                    background: btnHover ? lightTheme.surfaceHover : lightTheme.surface,
                    borderColor: btnHover ? lightTheme.borderStrong : lightTheme.border,
                }}
                onClick={() => setOpen(prev => !prev)}
                onMouseEnter={() => setBtnHover(true)}
                onMouseLeave={() => setBtnHover(false)}
                aria-label="Notifications"
            >
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unreadCount > 0 && <span style={S.dot} />}
            </button>

            {open && (
                <div style={S.panel}>
                    <div style={S.header}>
                        <span style={S.panelTitle}>Notifications</span>
                        <button
                            type="button"
                            style={{
                                ...S.markAll,
                                ...(unreadCount === 0 ? { color: lightTheme.textMuted, cursor: 'not-allowed' } : {}),
                                ...(markAllHover && unreadCount > 0 ? { background: lightTheme.primarySoft } : {}),
                            }}
                            onClick={markAllRead}
                            disabled={unreadCount === 0}
                            onMouseEnter={() => setMarkAllHover(true)}
                            onMouseLeave={() => setMarkAllHover(false)}
                        >
                            Mark all as read
                        </button>
                    </div>

                    <div style={S.panelBody}>
                        {loading ? (
                            <div style={S.state}>Loading…</div>
                        ) : error ? (
                            <div style={S.stateError}>{error}</div>
                        ) : notifications.length === 0 ? (
                            <div style={S.state}>No notifications</div>
                        ) : (
                            notifications.map(n => {
                                const isUnread = !isItemRead(n);
                                const sevStyle = severityStyle(n.severity);
                                return (
                                    <div
                                        key={n.id}
                                        style={{
                                            ...S.item,
                                            background: hoveredId === n.id ? lightTheme.surfaceHover : 'transparent',
                                        }}
                                        onMouseEnter={() => setHoveredId(n.id)}
                                        onMouseLeave={() => setHoveredId(null)}
                                        onClick={() => (n.link ? handleAction(n) : markRead(n.id))}
                                    >
                                        <div style={S.meta}>
                                            {isUnread && <span style={S.newBadge}>NEW</span>}
                                            {n.severity && sevStyle && <span style={sevStyle}>{n.severity}</span>}
                                            {n.category && <span style={S.category}>{n.category}</span>}
                                            {n.created_at && <span style={S.date}>{formatDate(n.created_at)}</span>}
                                        </div>
                                        {n.title && <div style={S.title}>{n.title}</div>}
                                        {n.body && <div style={S.desc}>{n.body}</div>}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
